import { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Heading,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { RoleBadge, StandsBadge } from "./LineBadges";
import { usePartyNames } from "../../hooks/usePartyNames";
import { getSignedLineAdvicesForPlacement } from "../../services/PlacingAPI";
import { showApiErrorToast } from "../../utils/showApiToast";
import { formatDate, readParticipation } from "./placingUtils";

const pct = (n) => (n == null ? "—" : `${Math.round(Number(n) * 10000) / 10000}%`);

/** "Signed down" banner, as shown wherever the placement's state is summarised. */
export const SignedDownBanner = ({ placement }) => (
  <Alert status="success" variant="left-accent" borderRadius="md">
    <AlertIcon />
    <Box>
      <AlertTitle>Signed down</AlertTitle>
      <AlertDescription fontSize="sm">
        Closed to further lines {formatDate(placement.signedDownDate) ? `on ${formatDate(placement.signedDownDate)}` : ""}
        {placement.signingPercentage != null &&
          (Number(placement.signingPercentage) === 100
            ? " · no signing down was needed"
            : ` · lines not standing signed at ${pct(placement.signingPercentage)} of written`)}
      </AlertDescription>
    </Box>
  </Alert>
);

/** Broker: the signed lines, from the placement's signed line advices. */
const SignedDownView = ({ placement }) => {
  const toast = useToast();
  const [advices, setAdvices] = useState(null);

  useEffect(() => {
    getSignedLineAdvicesForPlacement(placement._id)
      .then(setAdvices)
      .catch((err) => {
        setAdvices([]);
        showApiErrorToast(toast, err, { title: "Couldn't load the signed lines" });
      });
  }, [placement._id, toast]);

  const names = usePartyNames((advices || []).map((a) => a.receiver?.partyId));
  const sorted = [...(advices || [])].sort((a, b) => (a.receiver?.roleCode === "lead" ? 0 : 1) - (b.receiver?.roleCode === "lead" ? 0 : 1));
  const total = (field) => Math.round(sorted.reduce((t, a) => t + (Number(readParticipation(a)[field]) || 0), 0) * 10000) / 10000;

  return (
    <VStack align="stretch" spacing={3}>
      <SignedDownBanner placement={placement} />
      <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
        <Heading size="sm" mb={3}>Signed lines</Heading>
        {advices === null ? (
          <Spinner />
        ) : sorted.length === 0 ? (
          <Text fontSize="sm" color="gray.500">No signed line advices found.</Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr><Th>Carrier</Th><Th>Role</Th><Th isNumeric>Written %</Th><Th>Line to stand</Th><Th isNumeric>Signed %</Th><Th>Advice sent</Th></Tr>
            </Thead>
            <Tbody>
              {sorted.map((a) => {
                const p = readParticipation(a);
                return (
                  <Tr key={a._id}>
                    <Td fontWeight="semibold">{names[a.receiver?.partyId] || "Loading…"}</Td>
                    <Td><RoleBadge role={a.receiver?.roleCode} /></Td>
                    <Td isNumeric fontFamily="mono">{pct(p.writtenShare)}</Td>
                    <Td>
                      {p.lineToStandIndicator ? <StandsBadge /> : ""}
                      {a.acceptedConditions && (
                        <Text fontSize="xs" color="orange.700" whiteSpace="pre-wrap" mt={1}>Conditions accepted: {a.acceptedConditions}</Text>
                      )}
                    </Td>
                    <Td isNumeric fontFamily="mono" fontWeight="semibold">{pct(p.signedShare)}</Td>
                    <Td>{formatDate(a.receiver?.sentDate || a.createdDate)}</Td>
                  </Tr>
                );
              })}
            </Tbody>
            <Tfoot>
              <Tr><Th>Total</Th><Th /><Th isNumeric>{pct(total("writtenShare"))}</Th><Th /><Th isNumeric>{pct(total("signedShare"))}</Th><Th /></Tr>
            </Tfoot>
          </Table>
        )}
      </Box>
    </VStack>
  );
};

export default SignedDownView;
